import { create } from 'zustand';
import { AddressJusoDto } from 'src/dtos/address/addressApiResponseDto';
import { SysCodeDto } from 'src/dtos/code/sysCodeDto';
import { OrganizationDto } from 'src/dtos/organization/organizationDto';
import { OrganizationMapDto } from 'src/dtos/organization/organizationMapDto';
import { EmployeeDto } from 'src/dtos/employee/employeeDto';
import { SendTargetEmp } from 'src/dtos/popbill/CmailDto';
import { CodeSearchModalProps } from '../types/code-search';
import { ConfirmModalProps, defaultConfirmModalProps } from 'src/components/common/modal/ConfirmModal';

// 토스트 메시지 타입 정의
export type ToastType = 'success' | 'error' | 'warning' | 'info';

// 토스트 메시지 인터페이스
export interface ToastMessage {
	id: string;
	type: ToastType;
	message: string;
	duration?: number;
	createdAt: number;
}

export interface UiuxStore {

	tabList: Tab[];
	addTabList: (tab: Tab) => void;
	removeTabList: (key: string) => void;
	activeTab: Tab | undefined;
	setActiveTab: (activeTab: Tab | undefined) => void;
	resetTabList: () => void;

	codeSearchModalVisible: boolean;
	setCodeSearchModalVisible: (visible: boolean) => void;
	codeSearchModalProps: CodeSearchModalProps | undefined;
	setCodeSearchModalProps: (props: CodeSearchModalProps) => void;

	commonCodeModalVisible: boolean;
	setCommonCodeModalVisible: (visible: boolean) => void;
	commonCodeModalProps: CommonCodeModalProps | undefined;
	setCommonCodeModalProps: (props: CommonCodeModalProps) => void;

	sheetModalVisible: boolean;
	setSheetModalVisible: (visible: boolean) => void;
	sheetModalProps: SheetModalProps | undefined;
	setSheetModalProps: (props: SheetModalProps) => void;

	sidebarVisible: boolean;
	setSidebarVisible: (visible: boolean) => void;

	addressModalVisible: boolean;
	setAddressModalVisible: (visible: boolean) => void;
	addressModalProps: AddressModalProps | undefined;
	setAddressModalProps: (props: AddressModalProps | undefined) => void;

	organizaMapModalVisible: boolean;
	setOrganizaMapModalVisible: (visible: boolean) => void;
	organizaMapModalProps: OrganizaMapModalProps | undefined;
	setOrganizaMapModalProps: (props: OrganizaMapModalProps | undefined) => void;

	findAccountModalVisible: boolean;
	setFindAccountModalVisible: (findAccountModalVisible: boolean) => void;

	isPrinterSettingModalOpen: boolean;
	setIsPrinterSettingModalOpen: (visible: boolean) => void;

	authactionbarLogModalVisible: boolean;
	setAuthactionbarLogModalVisible: (visible: boolean) => void;

	confirmModalVisible: boolean;
	setConfirmModalVisible: (visible: boolean) => void;
	confirmModalTrigger: boolean;
	setConfirmModalTrigger: (trigger: boolean) => void;
	confirmModalProps: ConfirmModalProps | undefined;
	setConfirmModalProps: (props: ConfirmModalProps | undefined) => void;

	message: string;
	setMessage: (message: string) => void;

	// 토스트 메시지 관련
	toastMessages: ToastMessage[];
	addToast: (type: ToastType, message: string, duration?: number) => void;
	removeToast: (id: string) => void;
	clearToasts: () => void;

	// 페이지 간 데이터 전달 (KSK2101_R01 -> KSK2021_R01), (KSK2010_R01 -> KSK2020_R01)
	productWorkNavigationData: ProductWorkNavigationData | null;
	setProductWorkNavigationData: (data: ProductWorkNavigationData | null) => void;

	productMaterialNavigationData: ProductMaterialNavigationData | null;
	setProductMaterialNavigationData: (data: ProductMaterialNavigationData | null) => void;
	
	// 페이지 간 데이터 전달 (SAL6010 -> SAL1010)
	orderNavigationData: OrderNavigationData | null;
	setOrderNavigationData: (data: OrderNavigationData | null) => void;

	tabContextMenu: {
		visible: boolean;
		x: number;
		y: number;
	};
	setTabContextMenu: (menu: Partial<UiuxStore['tabContextMenu']>) => void;
	closeTabContextMenu: () => void;
	openTabContextMenu: (e: React.MouseEvent) => void;
	getTabContextMenuItems: () => Array<{
		label: string;
		onClick: () => void;
		disabled: boolean;
	}>;
	closeOtherTabs: () => void;
	closeRightTabs: () => void;
	closeAllTabs: () => void;
}

export interface Tab {
	key: string;
	title: string;
	component: React.FC | null;
	fixed?: boolean;
}

export interface UserModalProps {
	search: string;
	rowIndex: number;
}

export interface SheetModalProps {
	data: string[][];
	// data: string; // 새탭에서 엑셀열기, 대부분 호환이안돼서 다운로드로됨
}

export interface CommonCodeModalProps {
	data?: SysCodeDto
}



export interface AddressModalProps {
	onSelect: (data: AddressJusoDto) => void;
	handleFoucsId?: string;	// 주소 검색 후 입력 포커스 이동을 위한 아이디
}

export interface OrganizaMapModalProps {
	selectedEmpList: SendTargetEmp[];
	handleFoucsId?: string;	// 부서, 사원 지정 후 입력 포커스 이동을 위한 아이디
	onClear?: () => void;
	onConfirm: (data: SendTargetEmp[]) => void;
}

// 페이지 간 데이터 전달용 인터페이스
export interface ProductWorkNavigationData {
	productWorkNo: string;
	/** PRD형 화면(KSK2020/2110)용 작업지시번호 */
	productOrderNo?: string;
}

// 페이지 간 데이터 전달용 인터페이스
export interface ProductMaterialNavigationData {
	productWorkNo: string;
}

// 견적서에서 수주로 데이터 전달용 인터페이스
export interface OrderNavigationData {
	// 견적서 헤더 정보
	salesEstimateNo: string;
	compCd: string;
	customCd: string;
	customNm: string;
	estimateDt: string;
	vatType: string;
	moneyCd: string;
	telNo?: string;
	faxNo?: string;
	email?: string;
	post?: string;
	address?: string;
	detailAddress?: string;
	empNo?: string;
	empNm?: string;
	remark?: string;
	
	// 견적서 상세 정보
	detailList: {
		itemCd: string;
		itemNm: string;
		itemSpec: string;
		estimateUnit: string;
		estimateQty: number;
		estimatePrice: number;
		dcAmt: number;
		itemAmt: number;
		remark?: string;
	}[];
}

const useUiuxStore = create<UiuxStore>((set, get) => ({

	tabList: [],
	addTabList: (tab: Tab) => set((state) => {
		const exists = state.tabList.find(t => t.key === tab.key);
		return exists ? state : { ...state, tabList: [...state.tabList, tab] };
	}),
	removeTabList: (key: string) => set((state) => {

		let tabList = [...state.tabList];
		const removedIndex = tabList.findIndex(value => value.key === key);
		tabList = [...state.tabList].filter(value => value.key !== key);
		const activeKey = state.activeTab?.key;


		return {
			...state,
			tabList,
			activeTab: key !== activeKey ? state.activeTab : tabList[removedIndex] ? tabList[removedIndex] : tabList[removedIndex - 1],
			message: ""  // 탭 닫기 시 메시지 초기화
		}
	}),
	activeTab: undefined,
	setActiveTab: (activeTab: Tab | undefined) => set((state) => ({
		...state,
		activeTab,
		message: ""  // 탭 변경 시 메시지 초기화
	})),
	resetTabList: () => set({ tabList: [], activeTab: undefined }),

	codeSearchModalVisible: false,
	setCodeSearchModalVisible: (visible: boolean) => set((state) => {
		return {
			...state,
			codeSearchModalVisible: visible
		}
	}),
	codeSearchModalProps: undefined,
	setCodeSearchModalProps: (props: CodeSearchModalProps) => set((state) => {
		return {
			...state,
			codeSearchModalProps: props,
			codeSearchModalVisible: true
		}
	}),
	commonCodeModalVisible: false,
	setCommonCodeModalVisible: (visible: boolean) => set((state) => {
		return {
			...state,
			commonCodeModalProps: undefined,
			commonCodeModalVisible: visible
		}
	}),
	commonCodeModalProps: undefined,
	setCommonCodeModalProps: (props: CommonCodeModalProps) => set((state) => {
		return {
			...state,
			commonCodeModalProps: props,
			commonCodeModalVisible: true
		}
	}),

	sheetModalProps: undefined,
	setSheetModalProps: (props: SheetModalProps) => set((state) => {
		return {
			...state,
			sheetModalProps: props,
			sheetModalVisible: true
		}
	}),
	sheetModalVisible: false,
	setSheetModalVisible: (visible: boolean) => set((state) => {
		return {
			...state,
			sheetModalProps: undefined,
			sheetModalVisible: visible
		}
	}),

	sidebarVisible: true,
	setSidebarVisible: (visible: boolean) => set((state) => {

		localStorage.setItem('sidebar-visible', visible ? 'true' : 'false');

		return {
			...state,
			sidebarVisible: visible
		}
	}),

	addressModalVisible: false,
	setAddressModalVisible: (visible: boolean) => set((state) => {
		return {
			...state,
			addressModalVisible: visible
		}
	}),

	addressModalProps: undefined,
	setAddressModalProps: (props: AddressModalProps | undefined) => set((state) => {
		return {
			...state,
			addressModalProps: props,
			addressModalVisible: props ? true : false
		}
	}),

	findAccountModalVisible: false,
	setFindAccountModalVisible: (findAccountModalVisible: boolean) => set({ findAccountModalVisible }),

	isPrinterSettingModalOpen: false,
	setIsPrinterSettingModalOpen: (visible: boolean) => set((state) => ({
		...state,
		isPrinterSettingModalOpen: visible
	})),

	organizaMapModalVisible: false,
	setOrganizaMapModalVisible: (visible: boolean) => set((state) => {
		return {
			...state,
			organizaMapModalVisible: visible
		}
	}),

	authactionbarLogModalVisible: false,
	setAuthactionbarLogModalVisible: (visible: boolean) => set((state) => ({
		...state,
		authactionbarLogModalVisible: visible
	})),
	
	organizaMapModalProps: undefined,
	setOrganizaMapModalProps: (props: OrganizaMapModalProps | undefined) => set((state) => {
		return {
			...state,
			organizaMapModalProps: props,
			organizaMapModalVisible: props ? true : false
		}
	}),

	confirmModalVisible: false,
	setConfirmModalVisible: (visible: boolean) => set((state) => {
		return {
			...state,
			confirmModalVisible: visible
		}
	}),
	confirmModalTrigger: false,
	setConfirmModalTrigger: (trigger: boolean) => set((state) => {
		return {
			...state,
			confirmModalTrigger: trigger
		}
	}),
	confirmModalProps: defaultConfirmModalProps,
	setConfirmModalProps: (props: ConfirmModalProps | undefined) => set((state) => {
		return {
			...state,
			confirmModalProps: props,
		}
	}),

	message: "",
	setMessage: (message: string) => set((state) => {
		return {
			...state,
			message
		}
	}),

	// 토스트 메시지 관련 상태 및 메서드
	toastMessages: [],
	addToast: (type: ToastType, message: string, duration = 5000) => set((state) => {
		const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
		const newToast: ToastMessage = {
			id,
			type,
			message,
			duration,
			createdAt: Date.now()
		};

		// 자동 제거 타이머 설정
		setTimeout(() => {
			get().removeToast(id);
		}, duration);

		return {
			...state,
			toastMessages: [...state.toastMessages, newToast]
		};
	}),
	removeToast: (id: string) => set((state) => ({
		...state,
		toastMessages: state.toastMessages.filter(toast => toast.id !== id)
	})),
	clearToasts: () => set((state) => ({
		...state,
		toastMessages: []
	})),

	// 페이지 간 데이터 전달 관련
	productWorkNavigationData: null,
	setProductWorkNavigationData: (data: ProductWorkNavigationData | null) => set((state) => ({
		...state,
		productWorkNavigationData: data
	})),

	// 페이지 간 데이터 전달 관련
	productMaterialNavigationData: null,
	setProductMaterialNavigationData: (data: ProductMaterialNavigationData | null) => set((state) => ({
		...state,
		productMaterialNavigationData: data
	})),
	
	orderNavigationData: null,
	setOrderNavigationData: (data: OrderNavigationData | null) => set((state) => ({
		...state,
		orderNavigationData: data
	})),

	tabContextMenu: { visible: false, x: 0, y: 0 },
	setTabContextMenu: (menu: Partial<UiuxStore['tabContextMenu']>) => set(state => ({
		tabContextMenu: { ...state.tabContextMenu, ...menu }
	})),
	closeTabContextMenu: () => set({
		tabContextMenu: { visible: false, x: 0, y: 0 }
	}),

	openTabContextMenu: (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		const { getTabContextMenuItems, setTabContextMenu } = get();
		const items = getTabContextMenuItems();
		if (items.some(item => !item.disabled)) {
			setTabContextMenu({ visible: true, x: e.clientX, y: e.clientY });
		}
	},
	
	getTabContextMenuItems: () => {
		const { tabList, activeTab, closeOtherTabs, closeRightTabs, closeAllTabs } = get();
		const activeIndex = activeTab ? tabList.findIndex(t => t.key === activeTab.key) : -1;
		const closableTabs = tabList.filter(t => !t.fixed);
		const rightClosableTabs = activeIndex > -1 ? tabList.slice(activeIndex + 1).filter(t => !t.fixed) : [];

		return [
			{ label: '다른 모든 탭 닫기', onClick: closeOtherTabs, disabled: !activeTab || closableTabs.length <= 1 },
			{ label: '활성 탭 기준 우측 탭 닫기', onClick: closeRightTabs, disabled: !activeTab || activeIndex === -1 || rightClosableTabs.length === 0 },
			{ label: '모든 탭 닫기', onClick: closeAllTabs, disabled: closableTabs.length === 0 },
		];
	},

	closeOtherTabs: () => set(state => {
		if (!state.activeTab) return {};
		const newTabList = state.tabList.filter(tab => tab.fixed || tab.key === state.activeTab?.key);
		return { tabList: newTabList };
	}),
	
	closeRightTabs: () => set(state => {
		if (!state.activeTab) return {};
		const activeIndex = state.tabList.findIndex(tab => tab.key === state.activeTab?.key);
		if (activeIndex === -1) return {};

		const tabsOnLeft = state.tabList.slice(0, activeIndex + 1);
		const tabsOnRight = state.tabList.slice(activeIndex + 1);
		const fixedTabsOnRight = tabsOnRight.filter(tab => tab.fixed);
		
		const newTabList = [...tabsOnLeft, ...fixedTabsOnRight];
		return { tabList: newTabList };
	}),

	closeAllTabs: () => set(state => {
		const newTabList = state.tabList.filter(tab => tab.fixed);
		let newActiveTab = state.activeTab;

		if (newActiveTab && !newActiveTab.fixed) {
			newActiveTab = newTabList.length > 0 ? newTabList[0] : undefined;
		}
		
		return { tabList: newTabList, activeTab: newActiveTab };
	}),
}));

export default useUiuxStore;